import { useState } from 'react'
import type { CompareRating, CreateRatingRequest } from '@/types/compare'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface CompareRatingControlsProps {
  modelResponseId: string
  initialRating?: CompareRating | null
  onSubmit: (request: CreateRatingRequest) => Promise<void>
}

export function CompareRatingControls({
  modelResponseId,
  initialRating,
  onSubmit,
}: CompareRatingControlsProps) {
  const [ratingStars, setRatingStars] = useState(initialRating?.ratingStars ?? 0)
  const [thumbs, setThumbs] = useState<'up' | 'down' | undefined>(initialRating?.thumbs)
  const [isBestAnswer, setIsBestAnswer] = useState(initialRating?.isBestAnswer ?? false)
  const [comment, setComment] = useState(initialRating?.comment ?? '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(Boolean(initialRating))

  const handleSubmit = async () => {
    setSaving(true)
    try {
      await onSubmit({
        modelResponseId,
        ratingStars: ratingStars > 0 ? ratingStars : undefined,
        thumbs,
        isBestAnswer,
        comment: comment.trim() || undefined,
      })
      setSaved(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-2 border-t border-border pt-3" data-testid={`compare-rating-${modelResponseId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Stars</span>
        <div className="flex gap-1" data-testid={`compare-stars-${modelResponseId}`}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              aria-label={`Rate ${star} stars`}
              onClick={() => setRatingStars(star)}
              className={cn(
                'rounded px-1 text-sm',
                ratingStars >= star ? 'text-amber-500' : 'text-muted-foreground',
              )}
            >
              ★
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant={thumbs === 'up' ? 'default' : 'outline'}
          onClick={() => setThumbs('up')}
          data-testid={`compare-thumbs-up-${modelResponseId}`}
        >
          👍
        </Button>
        <Button
          type="button"
          size="sm"
          variant={thumbs === 'down' ? 'default' : 'outline'}
          onClick={() => setThumbs('down')}
          data-testid={`compare-thumbs-down-${modelResponseId}`}
        >
          👎
        </Button>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isBestAnswer}
          onChange={(event) => setIsBestAnswer(event.target.checked)}
          data-testid={`compare-best-answer-${modelResponseId}`}
        />
        Best answer
      </label>

      <textarea
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        placeholder="Comment (optional)"
        rows={2}
        className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
        data-testid={`compare-comment-${modelResponseId}`}
      />

      <Button
        type="button"
        size="sm"
        disabled={saving || (!ratingStars && !thumbs && !comment.trim())}
        onClick={handleSubmit}
        data-testid={`compare-submit-rating-${modelResponseId}`}
      >
        {saved ? 'Update rating' : 'Save rating'}
      </Button>
    </div>
  )
}
